import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DlBtn from '../DlBtn';
import { api } from '../../utils/api';

// The two closing buttons (owner 2026-08-16): a job can only be set to "6. Closed" once
//   ① Save all files — every business file archived into Document Storage (AI-typed, protocol-named)
//   ② Set Calendar — at least one reminder configured, or explicitly "No reminder needed"
// Both states live on the job; the lead can re-open details / re-run / change them any time.

type Requirement = { key: string; label: string; uploaded: boolean; saved: boolean; deliverable?: boolean };
type DocSend = { id: string; status: 'pending' | 'sent' | 'cancelled' | 'error'; dueAt: number; sentAt: number; byName: string; to: string[]; subject: string; count: number; error: string };
type State = {
  running: boolean; progress: { total: number; doneCount: number } | null;
  last: any | null; archiveDone: boolean; remindersCount: number; waived: boolean; calendarDone: boolean;
  gated?: boolean; requirements?: Requirement[]; requiredDone?: boolean; requirementsUnknown?: boolean;
  docSend?: DocSend | null;
  balanceDue?: number; owed?: boolean;
};
const fmtAmt = (n: number) => '฿' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtHM = (sec: number) => { const d = new Date(sec * 1000); const p = (n: number) => String(n).padStart(2, '0'); return `${p(d.getHours())}:${p(d.getMinutes())}`; };
const fmtSize = (b: number) => b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`;

const badge = (ok: boolean): React.CSSProperties => ({
  fontSize: 10, fontWeight: 800, letterSpacing: '.04em', borderRadius: 999, padding: '2px 8px',
  background: ok ? '#ECFDF5' : '#FFFBEB', color: ok ? '#15803D' : '#92400E', border: `1px solid ${ok ? '#86EFAC' : '#F1C232'}`,
});
const btn: React.CSSProperties = { padding: '5px 12px', borderRadius: 8, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer' };
const ghost: React.CSSProperties = { padding: '5px 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-secondary)', fontWeight: 700, fontSize: 12, cursor: 'pointer' };

export default function ClosingChecklist({ jobId, remindersVersion, onGated, readOnly }: { jobId: string; remindersVersion: number; onGated?: (g: boolean) => void; readOnly?: boolean }) {
  const { t } = useTranslation();
  const [s, setS] = useState<State | null>(null);
  const [details, setDetails] = useState(false);
  // Undo requires the operator's own login password (same gate as job delete).
  const [undoOpen, setUndoOpen] = useState(false);
  const [pw, setPw] = useState(''); const [pwBusy, setPwBusy] = useState(false); const [pwErr, setPwErr] = useState('');
  const timer = useRef<any>(null);
  const load = () => api.jobArchiveState(jobId).then((r: any) => { setS(r); onGated?.(r?.gated !== false); }).catch(() => {});
  useEffect(() => { load(); return () => clearTimeout(timer.current); }, [jobId, remindersVersion]); // eslint-disable-line
  useEffect(() => {
    if (s?.running) { timer.current = setTimeout(load, 2500); }
    return () => clearTimeout(timer.current);
  }, [s]); // eslint-disable-line

  // Two-phase save (owner 2026-08-16): preview first — every file's proposed name + destination is
  // shown and EDITABLE; only rows the operator ticks Confirm on are written. (Hooks live ABOVE the
  // early return — rules of hooks.)
  const [pv, setPv] = useState<any>(null);          // preview payload while the review modal is open
  const [, setPvPolling] = useState(false);
  const [edits, setEdits] = useState<Record<string, { name: string; folderId: string; checked: boolean }>>({});
  const [folderQ, setFolderQ] = useState('');
  const [committing, setCommitting] = useState(false);
  const pvTimer = useRef<any>(null);
  const pollPreview = () => {
    api.jobArchivePreview(jobId).then((r: any) => {
      if (r.status === 'running') { setPv(r); pvTimer.current = setTimeout(pollPreview, 2000); return; }
      if (r.status === 'ready') {
        setPv(r);
        const e: Record<string, { name: string; folderId: string; checked: boolean }> = {};
        for (const it of (r.items || [])) e[it.key] = { name: it.name, folderId: it.folderId, checked: false };
        setEdits(e);
      } else if (r.status === 'error') { window.alert(r.error || 'Preview failed'); setPv(null); }
      setPvPolling(false);
    }).catch(() => { setPvPolling(false); setPv(null); });
  };
  const run = async () => {
    try { await api.jobArchivePreviewStart(jobId); setPvPolling(true); setPv({ status: 'running' }); pollPreview(); }
    catch (e: any) { window.alert(e?.message || 'Failed to start'); }
  };
  useEffect(() => () => clearTimeout(pvTimer.current), []);
  // Documents → client (#50-B): operator reviews + confirms in a modal; the send is queued with a
  // 15-minute cancel window (server-side sweep sends it). Hooks above the early return.
  const [dm, setDm] = useState<any>(null);            // prepare payload while the modal is open
  const [dmTo, setDmTo] = useState(''); const [dmSubject, setDmSubject] = useState('');
  const [dmPicked, setDmPicked] = useState<Record<string, boolean>>({});
  const [dmBusy, setDmBusy] = useState(false); const [dmErr, setDmErr] = useState('');
  const docTimer = useRef<any>(null);
  useEffect(() => {   // pending countdown — refresh the row every 30s so "sends at HH:MM" resolves to sent
    if (s?.docSend?.status === 'pending') { docTimer.current = setTimeout(load, 30_000); }
    return () => clearTimeout(docTimer.current);
  }, [s]); // eslint-disable-line
  const openDocsModal = async () => {
    setDmErr('');
    try {
      const p = await api.jobDocsEmailPrepare(jobId);
      setDm(p); setDmTo((p.to || []).join(', ')); setDmSubject(p.subject || '');
      const picked: Record<string, boolean> = {};
      for (const f of (p.files || [])) picked[f.nodeId] = true;
      setDmPicked(picked);
    } catch (e: any) { window.alert(e?.message || 'Failed to prepare'); }
  };
  const confirmDocsSend = async () => {
    if (dmBusy) return;
    const nodeIds = Object.entries(dmPicked).filter(([, v]) => v).map(([k]) => k);
    setDmBusy(true); setDmErr('');
    try { await api.jobDocsEmailQueue(jobId, { to: dmTo, subject: dmSubject.trim(), nodeIds }); setDm(null); load(); }
    catch (e: any) { setDmErr(e?.message || 'Failed'); }
    finally { setDmBusy(false); }
  };
  const cancelDocsSend = async () => { try { await api.jobDocsEmailCancel(jobId); } catch { /* */ } load(); };

  const folderPaths = useMemo(() => {
    const list: Array<{ id: string; parent_id: string; name: string }> = pv?.folders || [];
    const byId: Record<string, any> = Object.fromEntries(list.map(f => [f.id, f]));
    const out: Record<string, string> = {};
    for (const f of list) {
      const parts = [f.name]; let cur = byId[f.parent_id]; let n = 0;
      while (cur && n++ < 12) { parts.unshift(cur.name); cur = byId[cur.parent_id]; }
      out[f.id] = parts.join(' / ');
    }
    return out;
  }, [pv?.folders]);

  if (!s) return null;   // after every hook — rules of hooks
  // Money still owed blocks "6. Closed" on EVERY job, gated or not (Calem, report 6585fc5a): the
  // server refuses the move until Balance Due is collected, so say so before anyone tries.
  const owedLine = s.owed && !readOnly ? (
    <div style={{ fontSize: 12, fontWeight: 700, color: '#DC2626', background: 'rgba(220,38,38,0.08)', border: '1px solid #DC2626', borderRadius: 8, padding: '7px 10px' }}>
      ⚠ {t('jb_close_owed', { amt: fmtAmt(s.balanceDue || 0) })}
    </div>
  ) : null;
  // Owner 2026-08-17: a job with NO linked workflow has no workflow documents and no reminder
  // obligation — the whole closing checklist is hidden and the server passes the Closed gate
  // unless money is owed, which is then the only line shown.
  if (s.gated === false) return owedLine;

  const commit = async () => {
    const items = Object.entries(edits).filter(([, v]) => v.checked && v.name.trim() && v.folderId)
      .map(([key, v]) => ({ key, name: v.name.trim(), folderId: v.folderId }));
    if (!items.length) { window.alert('Tick Confirm on at least one file.'); return; }
    setCommitting(true);
    try { await api.jobArchiveCommit(jobId, items); setPv(null); setEdits({}); load(); }
    catch (e: any) { window.alert(e?.message || 'Save failed'); }
    finally { setCommitting(false); }
  };
  const doUndo = async () => {
    if (!pw || pwBusy) return;
    setPwBusy(true); setPwErr('');
    try { await api.jobArchiveUndo(jobId, pw); setUndoOpen(false); setPw(''); load(); }
    catch (e: any) { setPwErr(e?.message || 'Undo failed'); }
    finally { setPwBusy(false); }
  };
  const waive = async (w: boolean) => { try { await api.jobRemindersWaive(jobId, w); } catch { /* */ } load(); };
  const counts = s.last ? `${s.last.archived} filed · ${s.last.fallback} to Working Documents · ${s.last.duplicates} already there${s.last.errors ? ` · ${s.last.errors} errors` : ''}` : '';

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '10px 12px', background: 'var(--bg-app)' }}>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '.03em', marginBottom: 8 }}>
        Closing checklist — required before "6. Closed"
        {readOnly && <span style={{ marginLeft: 8, fontWeight: 700, textTransform: 'none', letterSpacing: 0, color: '#92400E' }}>read-only — set the job back to "4. Complete" to change</span>}
      </div>
      {owedLine && <div style={{ marginBottom: 8 }}>{owedLine}</div>}
      {s.requirementsUnknown && (
        <div style={{ fontSize: 12, color: '#92400E', background: '#FFFBEB', border: '1px solid #F1C232', borderRadius: 8, padding: '7px 10px', marginBottom: 8 }}>
          ⚠ The required-document check could not run (public site unreachable) — closing is blocked until it can. Reload in a minute.
        </div>
      )}

      {/* ① files */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={badge(s.archiveDone)}>{s.archiveDone ? '✓ FILES SAVED' : '1 · FILES'}</span>
        {s.running ? (
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            <i className="ti ti-loader" style={{ animation: 'spin 1s linear infinite', display: 'inline-block', marginRight: 5 }} />
            Archiving… {s.progress ? `${s.progress.doneCount}/${s.progress.total}` : ''}
          </span>
        ) : (
          <>
            {!readOnly && <button onClick={run} style={s.archiveDone ? ghost : btn}>{s.archiveDone ? 'Re-run' : 'Save all files'}</button>}
            {s.last && <button onClick={() => setDetails(true)} style={{ ...ghost, borderStyle: 'dashed' }}>View details</button>}
            {!readOnly && s.archiveDone && <button onClick={() => { setPw(''); setPwErr(''); setUndoOpen(true); }} style={{ ...ghost, color: '#c0453f', borderColor: 'rgba(192,69,63,0.4)' }}>Undo</button>}
            {s.last && <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{counts}</span>}
          </>
        )}
      </div>

      {/* ①b ONLY what still needs saving (owner 2026-08-24: "除了显示我们需要保存的文件名，其他
          文件名字都不需要显示了"). That is: an SLF deliverable that HAS an upload but was never
          archived — the one thing standing between this job and "6. Closed".
          Deliberately hidden now: slots nobody uploaded to (nothing to save), slots already saved
          (the FILES badge above says so), and every client-supplied slot (they stopped blocking
          Closed the same day). The list empties itself as the work gets done. */}
      {(() => {
        const todo = (s.requirements || []).filter(r => r.deliverable !== false && r.uploaded && !r.saved);
        if (!todo.length) return null;
        return (
          <div style={{ marginTop: 7, marginLeft: 2, display: 'flex', flexWrap: 'wrap', gap: 5, alignItems: 'center' }}>
            <span style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '.03em', marginRight: 3 }}>Still to save</span>
            {todo.map(r => (
              <span key={r.key} title='Uploaded on the workflow but NOT saved yet — run "Save all files" and confirm it'
                style={{ fontSize: 11, fontWeight: 600, borderRadius: 999, padding: '2px 9px', background: '#FFFBEB', color: '#92400E', border: '1px solid #F1C232', maxWidth: 340, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                ⚠ {r.label}
              </span>
            ))}
            <span style={{ fontSize: 11.5, color: '#92400E', fontWeight: 600 }}>— this blocks Closed</span>
          </div>
        );
      })()}

      {/* ② calendar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
        <span style={badge(s.calendarDone)}>{s.calendarDone ? '✓ CALENDAR SET' : '2 · CALENDAR'}</span>
        {s.remindersCount > 0
          ? <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{s.remindersCount} active reminder(s) — manage below</span>
          : s.waived
            ? <><span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Marked as "no reminder needed"</span>
                {!readOnly && <button onClick={() => waive(false)} style={ghost}>Undo</button>}</>
            : readOnly
              ? <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>No reminder configured</span>
              : <><span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Add a reminder below, or:</span>
                <button onClick={() => waive(true)} style={ghost}>No reminder needed</button></>}
      </div>

      {/* ③ documents → client (optional, not gated): confirm in the modal → sends after a
          15-minute cancel window. Only offered once files are archived. */}
      {s.archiveDone && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
          <span style={badge(s.docSend?.status === 'sent')}>{s.docSend?.status === 'sent' ? '✓ DOCS EMAILED' : '3 · DOCS → CLIENT'}</span>
          {s.docSend?.status === 'pending' ? (
            <>
              <span style={{ fontSize: 12, color: '#92400E', fontWeight: 600 }}>
                Sends at {fmtHM(s.docSend.dueAt)} to {s.docSend.to.join(', ')} ({s.docSend.count} file(s))
              </span>
              <button onClick={cancelDocsSend} style={{ ...ghost, color: '#c0453f', borderColor: 'rgba(192,69,63,0.4)' }}>Cancel</button>
            </>
          ) : s.docSend?.status === 'sent' ? (
            <>
              <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                Sent to {s.docSend.to.join(', ')} at {fmtHM(s.docSend.sentAt)} · {s.docSend.count} file(s) · by {s.docSend.byName}
              </span>
              {!readOnly && <button onClick={openDocsModal} style={ghost}>Send again</button>}
            </>
          ) : (
            <>
              {!readOnly && <button onClick={openDocsModal} style={btn}>Email documents to client</button>}
              {readOnly && <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Not sent</span>}
              {s.docSend?.status === 'error' && <span style={{ fontSize: 12, color: '#c0453f' }}>Last attempt failed: {s.docSend.error}</span>}
            </>
          )}
        </div>
      )}

      {/* ── Documents → client modal: review recipients/subject/attachments, then confirm ── */}
      {dm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}
          onMouseDown={e => { if (e.target === e.currentTarget && !dmBusy) setDm(null); }}>
          <div style={{ width: 660, maxWidth: '96vw', maxHeight: '90vh', overflowY: 'auto', background: 'var(--bg-surface)', borderRadius: 14, border: '1px solid var(--border)', padding: 20, boxShadow: 'var(--shadow-md)' }}>
            <div style={{ fontSize: 14.5, fontWeight: 800 }}>Email documents to the client</div>
            <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 3 }}>
              After you confirm, the email sends automatically in {dm.delayMinutes} minutes — you can cancel it from the checklist during that window.
            </div>
            {dm.warning && <div style={{ fontSize: 12, color: '#92400E', background: '#FFFBEB', border: '1px solid #F1C232', borderRadius: 8, padding: '6px 10px', marginTop: 8 }}>{dm.warning}</div>}
            <div style={{ marginTop: 12 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-tertiary)' }}>TO</label>
              <input value={dmTo} onChange={e => setDmTo(e.target.value)} placeholder="client@example.com"
                style={{ width: '100%', padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border-strong)', fontSize: 13, background: 'var(--bg-app)', color: 'var(--text-primary)' }} />
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 3 }}>Cc: {(dm.cc || []).join(', ')} (office copy — also files this email into the job's threads)</div>
            </div>
            <div style={{ marginTop: 10 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-tertiary)' }}>SUBJECT</label>
              <input value={dmSubject} onChange={e => setDmSubject(e.target.value)}
                style={{ width: '100%', padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border-strong)', fontSize: 13, background: 'var(--bg-app)', color: 'var(--text-primary)' }} />
            </div>
            <div style={{ marginTop: 10 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-tertiary)' }}>ATTACHMENTS</label>
              {(dm.files || []).map((f: any) => (
                <label key={f.nodeId} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 2px', fontSize: 12.5, cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!dmPicked[f.nodeId]} onChange={e => setDmPicked(p => ({ ...p, [f.nodeId]: e.target.checked }))} />
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                  <span style={{ color: 'var(--text-tertiary)', flexShrink: 0 }}>{fmtSize(f.size)}</span>
                </label>
              ))}
              {(() => {
                const total = (dm.files || []).filter((f: any) => dmPicked[f.nodeId]).reduce((n: number, f: any) => n + (f.size || 0), 0);
                const over = total > (dm.maxBytes || 25 * 1048576);
                return <div style={{ fontSize: 11.5, marginTop: 4, color: over ? '#c0453f' : 'var(--text-tertiary)', fontWeight: over ? 700 : 400 }}>
                  Total: {fmtSize(total)} {over ? '— over the 25 MB limit, deselect some files' : '(limit 25 MB)'}
                </div>;
              })()}
            </div>
            <div style={{ marginTop: 10 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-tertiary)' }}>MESSAGE PREVIEW (edit the template in /admin)</label>
              <div className="rte-render" style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', fontSize: 12.5, maxHeight: 220, overflowY: 'auto', background: '#fff', color: '#1F2937' }}
                dangerouslySetInnerHTML={{ __html: dm.bodyHtml || '' }} />
            </div>
            {dmErr && <div style={{ fontSize: 12, color: '#c0453f', marginTop: 8 }}>{dmErr}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
              <button disabled={dmBusy} onClick={() => setDm(null)} style={ghost}>Close</button>
              <button disabled={dmBusy} onClick={confirmDocsSend} style={{ ...btn, opacity: dmBusy ? 0.6 : 1 }}>
                {dmBusy ? <><i className="ti ti-loader" style={{ animation: 'spin 1s linear infinite', display: 'inline-block', marginRight: 5 }} />Queuing…</> : `Confirm — send in ${dm.delayMinutes} min`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Review & confirm modal: nothing is saved until the operator confirms each row ── */}
      {pv && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}
          onMouseDown={e => { if (e.target === e.currentTarget && !committing && pv.status !== 'running') { setPv(null); } }}>
          <div style={{ width: 900, maxWidth: '96vw', maxHeight: '90vh', overflowY: 'auto', background: 'var(--bg-surface)', borderRadius: 14, border: '1px solid var(--border)', padding: 20, boxShadow: 'var(--shadow-md)' }}>
            <div style={{ fontSize: 14.5, fontWeight: 800 }}>Review before saving — nothing is stored until you confirm</div>
            {pv.status === 'running' ? (
              <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>
                <i className="ti ti-loader" style={{ animation: 'spin 1s linear infinite', display: 'inline-block', marginRight: 6 }} />
                AI is reading the files… {pv.done || 0}/{pv.total || '?'}
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '10px 0' }}>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
                    <input type="checkbox"
                      checked={!!Object.keys(edits).length && Object.values(edits).every(v => v.checked)}
                      onChange={e => setEdits(p => Object.fromEntries(Object.entries(p).map(([k, v]) => [k, { ...v, checked: e.target.checked }])))} />
                    Select all
                  </label>
                  <input value={folderQ} onChange={e => setFolderQ(e.target.value)} placeholder="Filter destination folders…"
                    style={{ marginLeft: 'auto', width: 240, padding: '6px 9px', fontSize: 12, border: '1px solid var(--border-strong)', borderRadius: 8, background: 'var(--c-gray-50)' }} />
                </div>
                {(pv.items || []).map((it: any) => {
                  const e = edits[it.key] || { name: it.name, folderId: it.folderId, checked: false };
                  const opts = (pv.folders || []).map((f: any) => ({ id: f.id, path: folderPaths[f.id] || f.name }))
                    .filter((o: any) => !folderQ.trim() || o.path.toLowerCase().includes(folderQ.toLowerCase()) || o.id === e.folderId)
                    .sort((a: any, b: any) => a.path.localeCompare(b.path));
                  return (
                    <div key={it.key} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 0', borderTop: '1px solid var(--border)' }}>
                      <input type="checkbox" checked={e.checked} onChange={ev => setEdits(p => ({ ...p, [it.key]: { ...e, checked: ev.target.checked } }))} style={{ marginTop: 6 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                          {it.src} · {it.original}
                          {it.confidence && <span style={{ marginLeft: 6, fontWeight: 700, color: it.confidence === 'high' ? '#15803D' : it.confidence === 'medium' ? '#B45309' : '#DC2626' }}>auto: {it.confidence}</span>}
                          {it.note && <span style={{ marginLeft: 6, color: '#B45309' }}>{it.note}</span>}
                          {it.dupe && <span style={{ marginLeft: 6, color: 'var(--text-tertiary)' }}>already in that folder — will be skipped</span>}
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 4 }}>
                          <div>
                            <div style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>Save as</div>
                            <input value={e.name} onChange={ev => setEdits(p => ({ ...p, [it.key]: { ...e, name: ev.target.value } }))}
                              style={{ width: '100%', padding: '7px 9px', fontSize: 12.5, border: '1px solid var(--border-strong)', borderRadius: 8, background: '#fff', boxSizing: 'border-box' }} />
                          </div>
                          <div>
                            <div style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>Location</div>
                            <select value={e.folderId} onChange={ev => setEdits(p => ({ ...p, [it.key]: { ...e, folderId: ev.target.value } }))}
                              style={{ width: '100%', padding: '7px 9px', fontSize: 12.5, border: '1px solid var(--border-strong)', borderRadius: 8, background: '#fff', boxSizing: 'border-box' }}>
                              {e.folderId && !opts.some((o: any) => o.id === e.folderId) && <option value={e.folderId}>{folderPaths[e.folderId] || '(current)'}</option>}
                              {opts.map((o: any) => <option key={o.id} value={o.id}>{o.path}</option>)}
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {!(pv.items || []).length && <div style={{ padding: 20, fontSize: 12.5, color: 'var(--text-tertiary)' }}>No files found for this job.</div>}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
                  <button onClick={() => !committing && setPv(null)} style={{ ...ghost, padding: '8px 16px' }}>Cancel</button>
                  <button onClick={commit} disabled={committing || !Object.values(edits).some(v => v.checked)}
                    style={{ padding: '8px 20px', borderRadius: 10, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', opacity: committing || !Object.values(edits).some(v => v.checked) ? 0.6 : 1 }}>
                    {committing ? 'Saving…' : `Save ${Object.values(edits).filter(v => v.checked).length} confirmed file(s)`}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {undoOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}
          onMouseDown={e => { if (e.target === e.currentTarget && !pwBusy) setUndoOpen(false); }}>
          <div style={{ width: 400, maxWidth: '92vw', background: 'var(--bg-surface)', borderRadius: 14, border: '1px solid var(--border)', padding: 22, boxShadow: 'var(--shadow-md)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <i className="ti ti-alert-triangle" style={{ fontSize: 18, color: '#E24B4A' }} aria-hidden />
              <div style={{ fontSize: 15, fontWeight: 800, color: '#E24B4A' }}>Undo archive</div>
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 14, lineHeight: 1.5 }}>
              This DELETES every file this job's archive created in Document Storage (originals in the job stay untouched) and returns the job to the un-archived state. Enter YOUR login password to confirm.
            </div>
            <input type="password" autoFocus value={pw} onChange={e => setPw(e.target.value)} placeholder="Your login password…"
              onKeyDown={e => e.key === 'Enter' && doUndo()} style={{ width: '100%', padding: '9px 11px', fontSize: 13, border: '1px solid var(--border-strong)', background: 'var(--c-gray-50)', borderRadius: 9, boxSizing: 'border-box' }} />
            {pwErr && <div style={{ fontSize: 12, color: '#E24B4A', marginTop: 8 }}>{pwErr}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button onClick={() => !pwBusy && setUndoOpen(false)} style={{ ...ghost, padding: '8px 16px' }}>Cancel</button>
              <button onClick={doUndo} disabled={pwBusy || !pw} style={{ padding: '8px 18px', borderRadius: 10, border: 'none', background: '#E24B4A', color: '#fff', fontWeight: 700, fontSize: 13, cursor: pwBusy || !pw ? 'default' : 'pointer', opacity: pwBusy || !pw ? 0.6 : 1 }}>{pwBusy ? '…' : 'Undo archive'}</button>
            </div>
          </div>
        </div>
      )}

      {details && s.last && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}
          onMouseDown={e => { if (e.target === e.currentTarget) setDetails(false); }}>
          <div style={{ width: 640, maxWidth: '94vw', maxHeight: '85vh', overflowY: 'auto', background: 'var(--bg-surface)', borderRadius: 14, border: '1px solid var(--border)', padding: 20, boxShadow: 'var(--shadow-md)' }}>
            <div style={{ fontSize: 14.5, fontWeight: 800, marginBottom: 4 }}>Archive details</div>
            <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 10 }}>{counts} · run {new Date((s.last.started_at || 0) * 1000).toLocaleString('en-GB')} by {s.last.by_name || '—'}</div>
            {(s.last.items || []).map((it: any, i: number) => (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '8px 0', borderTop: '1px solid var(--border)', fontSize: 12 }}>
                <span style={{ flex: 'none', marginTop: 2, fontSize: 9.5, fontWeight: 800, borderRadius: 999, padding: '1px 7px',
                  background: it.status === 'archived' ? '#ECFDF5' : it.status === 'duplicate' ? 'var(--c-gray-100)' : it.status === 'fallback' ? '#FFFBEB' : '#FEF2F2',
                  color: it.status === 'archived' ? '#15803D' : it.status === 'duplicate' ? 'var(--text-tertiary)' : it.status === 'fallback' ? '#92400E' : '#DC2626' }}>{it.status.toUpperCase()}</span>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'baseline', flexWrap: 'wrap' }}>
                    <span style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>Saved as:</span>
                    {it.fileNodeId
                      ? <DlBtn onRun={() => api.filesDownload(it.fileNodeId, it.name || it.original)}
                          style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer', color: 'var(--accent)', fontWeight: 700, fontSize: 12.5, textAlign: 'left', wordBreak: 'break-all' }}
                          title="Open / download this file">{it.name || it.original}</DlBtn>
                      : <b style={{ fontSize: 12.5, wordBreak: 'break-all' }}>{it.name || it.original}</b>}
                  </div>
                  {it.folder && (
                    <div style={{ marginTop: 2, fontSize: 11.5 }}>
                      <i className="ti ti-folder" style={{ marginRight: 4, color: '#B45309' }} aria-hidden />
                      <span style={{ color: 'var(--text-tertiary)' }}>Location: </span>
                      <span style={{ fontWeight: 600 }}>Document Storage / {it.folder}</span>
                    </div>
                  )}
                  <div style={{ marginTop: 2, color: 'var(--text-tertiary)', fontSize: 11 }}>
                    from {it.src} · original file: {it.original}{it.error ? ` · ${it.error}` : ''}
                  </div>
                </div>
              </div>
            ))}
            {!(s.last.items || []).length && <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)' }}>No files found for this job — nothing to archive.</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
              <button onClick={() => setDetails(false)} style={{ ...ghost, padding: '8px 16px' }}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
